import classNames from 'classnames'
import { addYears, setMonth } from 'date-fns'
import { useCallback, useMemo, useRef, useState } from 'react'
import type { VFC } from 'react'
import { Button, ButtonSize } from '../../Button'
import { DropdownMenu } from '../../Menu'
import { DeprecatedMenuOption } from '../../MenuOption/deprecated'
import { DeprecatedSingleSelectOption } from '../../SingleSelect/deprecated/types'
import { selectors } from './styles'
import type { ExternalPickerProps, PickerProps } from './types'

const Picker: VFC<PickerProps> = ({
  focusDate,
  formatter,
  onChange,
  options,
}) => {
  const [open, setOpen] = useState(false)
  const selectedOption = useMemo(
    () => ({
      value: focusDate.toDateString(),
      label: formatter.format(focusDate),
    }),
    [focusDate, formatter]
  )

  const pickerOptions: Array<DeprecatedSingleSelectOption> = options.map(
    (option) => ({
      value: option.toDateString(),
      label: formatter.format(option),
    })
  )

  // Track whether we've scrolled for this open session
  const hasScrolledRef = useRef(false)

  // Callback ref that scrolls to the selected item when it mounts
  const selectedItemCallbackRef = useCallback(
    (element: HTMLLIElement | null) => {
      if (element && !hasScrolledRef.current) {
        hasScrolledRef.current = true
        requestAnimationFrame(() => {
          element.scrollIntoView({
            block: 'center',
            inline: 'nearest',
          })
        })
      }
    },
    []
  )

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen)
        if (!isOpen) {
          hasScrolledRef.current = false
        }
      }}
    >
      <DropdownMenu.Trigger asChild>
        <Button
          className={selectors.pickerTrigger}
          buttonText={selectedOption.label}
          name="date-picker-value"
          displayMenuIndicator={true}
          size={ButtonSize.Small}
          variant="ghost"
        />
      </DropdownMenu.Trigger>
      {/* NOTE: <DropdownMenu.Portal> is intentioanlly
        not here because it conflicts with the parent's
        portal (Tippy) where this component is solely
        implemented, resulting in this component unmounting
        when selecting a menu item.
        See https://github.com/opallabs/cabochon/pull/12840
      */}
      <DropdownMenu.Content
        className={classNames('date-picker-select-content', selectors.dropdown)}
        align="start"
        zIndex={9999}
        loop
      >
        {pickerOptions.map((option) => {
          const isSelected = selectedOption.value === option.value
          return (
            <DropdownMenu.Item
              asChild
              key={option.value}
              textValue={option.label}
              onSelect={() => {
                onChange(new Date(option.value))
                setOpen(false)
              }}
            >
              <DeprecatedMenuOption
                ref={isSelected ? selectedItemCallbackRef : null}
                selected={isSelected}
                id={option.value}
                optionText={option.label}
              />
            </DropdownMenu.Item>
          )
        })}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
Picker.displayName = 'Lapidary: Picker ← MonthSelector ← Calendar'

export const MonthPicker: VFC<ExternalPickerProps> = (props) => {
  const options: Array<Date> = []
  for (let n = 0; n <= 11; n += 1) {
    // @note `date-fns` automatically compensates for different month lengths.
    options.push(setMonth(props.focusDate, n))
  }

  const formatter = new Intl.DateTimeFormat(props.locale, { month: 'long' })

  return <Picker formatter={formatter} options={options} {...props} />
}
MonthPicker.displayName = 'Lapidary: MonthPicker ← MonthSelector ← Calendar'

const yearsBuffer = 3
export const YearPicker: VFC<ExternalPickerProps> = (props) => {
  const options: Array<Date> = []
  for (let offset = yearsBuffer * -1; offset <= yearsBuffer; offset += 1) {
    options.push(addYears(props.focusDate, offset))
  }

  const formatter = new Intl.DateTimeFormat(props.locale, { year: 'numeric' })

  return <Picker formatter={formatter} options={options} {...props} />
}
YearPicker.displayName = 'Lapidary: YearPicker ← MonthSelector ← Calendar'
